<script lang="ts">
    // needs to be outside of setup, because of `defineEmits`
    const events = ['click', 'hover', 'hoverLeave'] as const;
</script>
<script setup lang="ts">
    /**
     * implements color dots from design system
     * https://www.sketch.com/s/d5c5b81a-6387-4b69-a27e-19a117a47eb4/a/MmK8Pla#Comment
     */
    interface ColorProps {
        colorOption: OptionValue;
    }

    const props = defineProps<ColorProps>();

    const { t } = useLocales();
    const darkText = ref(false);
    const selected = computed(() => props.colorOption.isSelected.value);

    /**
     * Colors checked with https://webaim.org/resources/contrastchecker/
     *
     * Keep in alphabetic order
     */
    const colorsWithDarkText = [
        'atomic-neon',
        'banana-yellow',
        'bubble-gum-blue',
        'cirrus-cloud',
        'flame-orange',
        'hot-pink',
        'ice-blue',
        'ice-blue---carbon-black',
        'ice-blue-carbon-black',
        'jungle-green',
        'lilac',
        'lizard-lime',
        'marshmallow-pink',
        'metallic-turquoise',
        'mint',
        'neon-coral',
        'off-white',
        'pale-grey',
        'pebble-grey',
        'sandy-brown',
        'silver',
        'sunny-yellow',
        'vibrant-yellow',
        'white',
        'woom-white',
        'wild-violet',
    ];

    const colorClass = computed(() => {
        const color = props.colorOption.value.toLowerCase().replace(/[\s/]/g, '-');
        darkText.value = colorsWithDarkText.includes(color);
        return `color-${color}`;
    });

    const emit = defineEmits([...events]);

    const interaction = (type: (typeof events)[number], name = '') => {
        emit(type, name);
    };

    const isDisabled = ref(false);

    watch(
        () => props.colorOption.disabled,
        () => {
            isDisabled.value = props.colorOption.disabled.value;
        },
        { deep: true, immediate: true },
    );

    const a11yLabel = computed(() => {
        const availabilty = !isDisabled.value ? t('cta.color.inStock') : t('cta.color.outOfStock');
        return `${props.colorOption.value} - ${availabilty}`;
    });
</script>

<template>
    <button
        type="button"
        :class="[
            'atm-color group flex h-12 w-12 items-center justify-center rounded-full transition-transform duration-200 ease-out will-change-transform hover:scale-110 focus:scale-110 lg:h-11 lg:w-11',
            ,
            darkText ? 'text-black' : 'text-white',
            {
                'scale-110': !colorOption.disabled.value && selected,
            },
        ]"
        :title="a11yLabel"
        :aria-label="a11yLabel"
        @click="interaction('click', colorOption.value)"
        @mouseover.prevent="interaction('hover', colorOption.value)"
        @mouseleave.prevent="interaction('hoverLeave')">
        <span
            :class="[
                'color flex h-10 w-10 items-center justify-center rounded-full border-2 border-black fill-current stroke-current group-focus:border-2 group-focus-visible:border-0 lg:h-9 lg:w-9',
                colorClass,
            ]">
            <woom-icon-close :class="[' h-5 w-5', { hidden: !isDisabled }]" />
            <woom-icon-check :class="[' h-5 w-5', { hidden: !selected || isDisabled }]" />
        </span>
    </button>
</template>

<style scoped>
    /* on dark/black/mossgreen backgrounds */
    .color-swatch-bg-mossgreen .color,
    .color-swatch-bg-black .color {
        @apply border-white;
    }

    /* color matcher */
    .color-woom-white,
    .color-white,
    .color-off-white {
        @apply bg-woom-white;
    }

    .color-ice-blue,
    .color-ice-blue---carbon-black,
    .color-ice-blue-carbon-black {
        @apply bg-woom-blue-ice;
    }

    .color-silver {
        @apply bg-woom-white-smoke;
    }

    .color-woom-red,
    .color-woom-red-matt,
    .color-red,
    .color-tropical-fun-starter-set {
        @apply bg-woom-red;
    }

    .color-black,
    .color-rolling-rebels {
        @apply bg-woom-black;
    }
    .color-charcoal,
    .color-charcoal-matt {
        @apply bg-woom-black-charcoal;
    }

    .color-red {
        @apply bg-woom-red-formula;
    }

    .color-electric-blue {
        @apply bg-woom-blue-electric;
    }

    .color-woom-blue {
        @apply bg-woom-blue;
    }

    .color-woom-green {
        @apply bg-woom-green;
    }

    .color-woom-yellow {
        @apply bg-woom-yellow;
    }

    .color-woom-purple,
    .color-purple-haze {
        @apply bg-woom-purple-haze;
    }

    .color-moon-grey {
        @apply bg-woom-grey-moon;
    }

    .color-midnight-blue {
        @apply bg-woom-blue-midnight;
    }

    .color-mint-green {
        @apply bg-woom-green-mint;
    }

    .color-sky-blue {
        @apply bg-woom-blue;
    }

    .color-sunny-yellow,
    .color-yellow {
        @apply bg-woom-yellow;
    }

    .color-woom-pink,
    .color-pink,
    .color-power-pink {
        @apply bg-woom-pink-power;
    }

    .color-woom-grey,
    .color-grey,
    .color-light-grey {
        @apply bg-woom-grey-mid;
    }

    .color-moss-green,
    .color-moss-green---formular-red,
    .color-moss-green-formular-red,
    .color-moss-green---formula-red,
    .color-moss-green-formula-red {
        @apply bg-woom-green-moss;
    }

    .color-terra-coppa,
    .color-terra---coppa {
        @apply bg-woom-terra-coppa;
    }

    .color-anniversary-red,
    .color-anniversary---red {
        @apply bg-woom-red-anniversary;
    }

    .color-green {
        background-color: #00a695;
    }
    .color-navy {
        background-color: navy;
    }

    .color-lizard-lime {
        @apply bg-woom-green-lizard-lime;
    }
    .color-flame-orange {
        @apply bg-woom-orange-flame;
    }

    .color-banana-yellow {
        @apply bg-woom-yellow-banana;
    }
    .color-wild-violet {
        @apply bg-woom-violet-wild;
    }
    .color-bubble-gum-blue {
        @apply bg-woom-blue-bubble-gum;
    }
    .color-pebble-grey {
        @apply bg-woom-grey-pebble;
    }
    .color-magnetic-blue,
    .color-magnetic-blue-matt {
        @apply bg-woom-blue-magnetic;
    }
    .color-jungle-green,
    .color-jungle-green-matt {
        @apply bg-woom-green-jungle;
    }
    .color-neon-coral,
    .color-neon-coral-matt {
        @apply bg-woom-coral-neon;
    }
    .color-sandy-brown {
        @apply bg-woom-brown-sandy;
    }
    .color-marshmallow-pink {
        @apply bg-woom-pink-marshmallow;
    }
    .color-metallic-turquoise {
        @apply bg-woom-turquoise-metallic;
    }
    .color-hot-pink {
        @apply bg-woom-pink-hot;
    }
    .color-vibrant-yellow {
        @apply bg-woom-yellow-vibrant;
    }
    .color-metallic-blue {
        @apply bg-woom-blue-metallic;
    }
    .color-mint {
        @apply bg-woom-green-mint-ready;
    }
    .color-lilac {
        @apply bg-woom-purple-lilac;
    }
    .color-pale-grey {
        @apply bg-woom-grey-pale;
    }
    .color-charcoal-metallic {
        @apply bg-woom-black-charcoal-metallic;
    }
    .color-graphite-grey {
        @apply bg-woom-grey-graphite;
    }
    .color-cirrus-cloud {
        @apply bg-woom-grey-cirrus-cloud;
    }

    /* gradients */
    .color-cosmic-blurple {
        @apply bg-gradient-to-r from-woom-blue-cosmic-blurple to-woom-purple-cosmic-blurple;
    }

    .color-red-eclipse {
        @apply bg-gradient-to-r from-black to-woom-red;
    }
    .color-purple-twilight {
        @apply bg-gradient-to-r from-black to-woom-purple-cosmic-blurple;
    }
    .color-atomic-neon {
        @apply bg-gradient-to-r from-woom-green-atomic-neon to-woom-yellow-atomic-neon;
    }
</style>
